// Auth modul: register, login, logout, getToken, setToken, Google auth

import { apiCall, getBaseUrl } from './api.js';

/** Több tenant ugyanazzal a Google emaillel – a felhasználónak ki kell választania. */
export class GoogleTenantPickRequired extends Error {
    readonly idToken: string;
    readonly tenantIds: string[];

    constructor(idToken: string, tenantIds: string[]) {
        super('Tenant választás szükséges');
        this.name = 'GoogleTenantPickRequired';
        this.idToken = idToken;
        this.tenantIds = tenantIds;
    }
}

function getToken(): string | null {
    return sessionStorage.getItem('glc_jwt');
}

function setToken(token: string): void {
    sessionStorage.setItem('glc_jwt', token);
}

function removeToken(): void {
    sessionStorage.removeItem('glc_jwt');
}

async function register(
    username: string, 
    tenantId: string, 
    email: string, 
    password: string, 
    passwordConfirm: string, 
    acceptedTerms: boolean
): Promise<boolean> {
    await apiCall('/auth/register', {
        method: 'POST',
        body: { 
            username, 
            tenant_id: tenantId, 
            email, 
            password, 
            password_confirm: passwordConfirm,
            accepted_privacy_and_terms: acceptedTerms 
        },
    });
    return true;
}

async function login(tenantId: string, userId: string, password: string): Promise<string> {
    const response = await apiCall('/auth/login', {
        method: 'POST',
        body: { tenant_id: tenantId, user_id: userId, password },
    });
    
    const data = await response.json();
    const token = data.access_token;
    setToken(token);
    return token;
}

async function logout(): Promise<void> {
    removeToken();
}

export type GoogleAuthResult = {
    access_token: string;
    user_id: string;
    tenant_id: string;
    role?: string;
};

/**
 * POST /auth/google – Google ID token → saját JWT (login | register).
 * 409 + tenant_ids → GoogleTenantPickRequired
 */
async function googleAuth(
    idToken: string,
    flow: 'login' | 'register',
    tenantId?: string
): Promise<GoogleAuthResult> {
    const baseUrl = getBaseUrl();
    const body: Record<string, string> = { id_token: idToken, flow };
    const t = tenantId?.trim().toLowerCase();
    if (t) body.tenant_id = t;

    let res: Response;
    try {
        res = await fetch(`${baseUrl}/auth/google`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
    } catch {
        throw new Error('Nem sikerült elérni a szervert (Google belépés). Ellenőrizd a VITE_API_BASE_URL beállítást.');
    }

    let data: any = {};
    try {
        data = await res.json();
    } catch {
        /* empty */
    }

    if (
        res.status === 409 &&
        data?.detail &&
        typeof data.detail === 'object' &&
        Array.isArray(data.detail.tenant_ids)
    ) {
        throw new GoogleTenantPickRequired(idToken, data.detail.tenant_ids as string[]);
    }

    if (!res.ok) {
        const msg =
            typeof data.detail === 'string'
                ? data.detail
                : data.detail != null
                  ? JSON.stringify(data.detail)
                  : `Szerver hiba (${res.status})`;
        throw new Error(msg);
    }

    const token = data.access_token as string;
    if (!token) throw new Error('Hiányzó access_token a válaszból.');
    setToken(token);
    return data as GoogleAuthResult;
}

export { getToken, setToken, removeToken, register, login, logout, googleAuth };
